import { createContext, useContextSelector } from 'use-context-selector'

import type { ValueType } from 'components/uiKit/KitTypes'
import { KitSize } from 'components/uiKit/KitTypes'
import { NOOP } from 'constants/commonConstans'

import type { IMenuRef } from '../Menu'
import type { ISelectProps } from './Select'
import type { SelectValueType, ISelectOption } from './SelectTypes'

export interface ISelectContext<V extends SelectValueType> extends ISelectProps<V> {
  map: Record<string, ISelectOption<V>>
  listOptions: ISelectOption<V>[]
  flattenOptions: Omit<ISelectOption<V>, 'options'>[]
  inputRef: React.RefObject<HTMLInputElement>
  listRef: React.RefObject<IMenuRef<ValueType>>
  inputValue: string
  forceSearch: boolean
  isOpen: boolean
  focus: boolean
  hideList: boolean
  maxLength?: number
  actions: {
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    setInputValue: React.Dispatch<React.SetStateAction<string>>
    setForceSearch: React.Dispatch<React.SetStateAction<boolean>>
  }
  handlers: {
    handleChooseOption: (option: ISelectOption<V>) => void
    handleChange: (value: V) => void
    handleSeparate: (value?: string) => void
    handleMouseUp: () => void
  }
}

const SelectContext = createContext<ISelectContext<SelectValueType>>({
  name: 'Select',
  listRef: { current: null },
  inputRef: { current: null },
  map: {},
  listOptions: [],
  flattenOptions: [],
  forceSearch: false,
  isOpen: false,
  focus: false,
  hideList: false,
  inputValue: '',
  maxLength: undefined,
  actions: {
    setIsOpen: NOOP,
    setForceSearch: NOOP,
    setInputValue: NOOP,
  },
  handlers: {
    handleChange: NOOP,
    handleChooseOption: NOOP,
    handleSeparate: NOOP,
    handleMouseUp: NOOP,
  },
})

export const useSelectActions = () => useContextSelector(SelectContext, (c) => c.actions)
export const useSelectHandlers = () => useContextSelector(SelectContext, (c) => c.handlers)
export const useSelectContext = <R,>(selector: (c: ISelectContext<SelectValueType>) => R) =>
  useContextSelector(SelectContext, selector)

export const useSelectInputRef = () => useContextSelector(SelectContext, (c) => c.inputRef)
export const useSelectListRef = () => useContextSelector(SelectContext, (c) => c.listRef)

export const useSelectSize = () => useContextSelector(SelectContext, (c) => c.size || KitSize.M)
export const useSelectStyleType = () =>
  useContextSelector(SelectContext, (c) => c.styleType || 'base')

export default SelectContext
